import { Box, Fab, Typography, useTheme } from "@mui/material";
import { CreateSuspectDialog } from "../create-suspect/CreateSuspect";
import { useState } from "react";
import {
  Close as CloseIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "../../store/slices/authSlice";
import { useFabActions, useRegisterFabAction } from "./FabActionsContext";

const FAB_SIZE = 56;
const ITEMS_PER_RING = [3, 5, 7];
const RING_BASE_RADIUS = 100;
const RING_SPACING = 100;
const START_ANGLE = 165;
const END_ANGLE = 285;

export default function ActionSideBar() {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [showSuspectDialog, setShowSuspectDialog] = useState(false);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Register the global "Skapa suspect" action
  useRegisterFabAction({
    id: "create-suspect",
    label: "Skapa suspekt",
    icon: <PersonAddIcon />,
    onClick: () => setShowSuspectDialog(true),
    priority: 0,
  });

  // All actions: global + page-injected via context
  const actions = useFabActions();

  const handleActionClick = (onClick: () => void) => {
    setIsOpen(false);
    onClick();
  };

  // Distributes items across concentric rings with 3-5-7 pattern.
  const getItemRing = (index: number) => {
    let remaining = index;
    for (let ring = 0; ring < ITEMS_PER_RING.length; ring++) {
      if (remaining < ITEMS_PER_RING[ring]) {
        return { ring, indexInRing: remaining, capacity: ITEMS_PER_RING[ring] };
      }
      remaining -= ITEMS_PER_RING[ring];
    }
    const lastCap = ITEMS_PER_RING[ITEMS_PER_RING.length - 1];
    const extraRings = Math.floor(remaining / lastCap);
    return {
      ring: ITEMS_PER_RING.length + extraRings,
      indexInRing: remaining % lastCap,
      capacity: lastCap,
    };
  };

  const getItemPosition = (index: number) => {
    const { ring, indexInRing, capacity } = getItemRing(index);
    const ringStart = ITEMS_PER_RING.slice(0, ring).reduce((a, b) => a + b, 0);
    const itemsInThisRing = Math.min(capacity, actions.length - ringStart);
    const radius = RING_BASE_RADIUS + ring * RING_SPACING;

    const angle =
      itemsInThisRing === 1
        ? START_ANGLE + (END_ANGLE - START_ANGLE) / 2
        : START_ANGLE +
          ((END_ANGLE - START_ANGLE) / (itemsInThisRing - 1)) * indexInRing;

    const rad = (angle * Math.PI) / 180;
    return {
      x: Math.cos(rad) * radius,
      y: Math.sin(rad) * radius,
      ring,
    };
  };

  const totalItems = ITEMS_PER_RING.reduce((a, b) => a + b, 0);
  const totalRings =
    actions.length <= totalItems
      ? ITEMS_PER_RING.findIndex(
          (_, i) =>
            ITEMS_PER_RING.slice(0, i + 1).reduce((a, b) => a + b, 0) >=
            actions.length
        ) + 1
      : ITEMS_PER_RING.length +
        Math.ceil(
          (actions.length - totalItems) /
            ITEMS_PER_RING[ITEMS_PER_RING.length - 1]
        );
  const maxRadius = RING_BASE_RADIUS + (totalRings - 1) * RING_SPACING + 60;
  const circleSize = maxRadius * 2;

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {isOpen && (
        <Box
          onClick={() => setIsOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: theme.zIndex.fab - 1,
            backgroundColor: "rgba(0,0,0,0.3)",
          }}
        />
      )}

      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 24, sm: 32 },
          right: { xs: 24, sm: 32 },
          zIndex: theme.zIndex.fab,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            bottom: FAB_SIZE / 2 - circleSize / 2,
            right: FAB_SIZE / 2 - circleSize / 2,
            width: circleSize,
            height: circleSize,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            transform: isOpen ? "scale(1)" : "scale(0)",
            transformOrigin: "50% 50%",
            transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            pointerEvents: "none",
          }}
        />

        {actions.map((action, idx) => {
          const pos = getItemPosition(idx);
          const delay = pos.ring * 0.08 + idx * 0.03 + 0.1;
          return (
            <Box
              key={action.id}
              onClick={() => handleActionClick(action.onClick)}
              sx={{
                position: "absolute",
                bottom: FAB_SIZE / 2,
                right: FAB_SIZE / 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                transform: isOpen
                  ? `translate(${pos.x}px, ${pos.y}px)`
                  : "translate(0, 0)",
                opacity: isOpen ? 1 : 0,
                transition: isOpen
                  ? `transform 0.35s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s, opacity 0.25s ease ${delay}s`
                  : "transform 0.25s ease, opacity 0.15s ease",
                zIndex: theme.zIndex.fab + 1,
                pointerEvents: isOpen ? "auto" : "none",
                "&:hover .fab-action-icon": {
                  transform: "scale(1.15)",
                },
              }}
            >
              <Box
                className="fab-action-icon"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.18)",
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  transition:
                    "transform 0.2s ease, background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                  },
                }}
              >
                {action.icon}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#fff",
                  mt: 0.25,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  userSelect: "none",
                }}
              >
                {action.label}
              </Typography>
            </Box>
          );
        })}

        <Fab
          aria-label={isOpen ? "Stäng meny" : "Öppna snabbmeny"}
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            width: FAB_SIZE,
            height: FAB_SIZE,
            boxShadow: 4,
            zIndex: theme.zIndex.fab + 2,
            position: "relative",
            transition: "background-color 0.3s ease",
            backgroundColor: isOpen ? "#fff" : "primary.main",
            color: isOpen ? "primary.main" : "#fff",
            "&:hover": {
              backgroundColor: isOpen ? "grey.100" : "primary.dark",
            },
          }}
        >
          {isOpen ? <CloseIcon /> : <AddIcon />}
        </Fab>
      </Box>

      <CreateSuspectDialog
        open={showSuspectDialog}
        onClose={() => setShowSuspectDialog(false)}
      />
    </>
  );
}
